/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext } from "react"

import _ from "lodash"
import useErrorHandling from "../../hooks/useErrorHandling"
import { FlightReports } from "../../api/FlightReport"
import { AirportSeasonConfigurations } from "../../api/AirportSeasonConfiguration"

import { getPageConfig } from "../../pages/Config"
import DownloadPane from "../../components/DownloadPane"

import { getAirportSeasonConfigurations } from "../../api/BackendCalls"
import { AppContext } from "../../app/App"
import { PageId } from "../../common/pageId"

import ReportSelector from "../../components/ReportSelector"
import { getFlightSelectorConfiguration } from "../../components/Utils"
import FlightSelector, {
  IFlightSelectorValues
} from "../../components/FlightSelector"
import Button from "../../components/generic/buttons/Button"
import { PageTemplate } from "../../components/pageTemplate/PageTemplate"
import { useFlightSelectorStateValues } from "../../hooks/useFlighSelectorInitValues"
import { handleDownload } from "../Support"
import store from "../../app/Store"
import { NotificationKind, notify } from "../../containers/notificationSlice"
import { PageContent } from "../../common/pageText"
import { FieldName, LoadingText } from "../../common/text"
import { CheckBooleanField } from "../../components/generic/form/CheckBooleanField"
import Card from "../../components/generic/Card"
import { useAppSelector } from "../../app/hooks"
import { EditMode } from "../../common/transaction"

interface Props {
  pageId: PageId
}

export const Download: React.FunctionComponent<Props> = ({ pageId }) => {
  const [airportSeasons, setAirportSeasons] =
    useState<AirportSeasonConfigurations>([])

  const pageConfig = getPageConfig[pageId]
  const pageTitle = pageConfig.pageTitle
  const codeType = pageConfig.codeType
  const flightSelectorConfig = pageConfig.flightSelectorConfig
  const airportseasonSelectorConfig = pageConfig.airportseasonSelectorConfig
  const appContext = useContext(AppContext)

  const { season, airports, countries, useLocalTime } =
    useAppSelector(
      (state) => state["selectionReducer"].airportSeasonsSelector
    ) || {}

  useEffect(() => {
    appContext.handleSetIsLoading({
      isLoading: true,
      loadingTxt: LoadingText.loadingAirportSeasons
    })
    getAirportSeasonConfigurations(
      pageId,
      (airportSeasons) => {
        setAirportSeasons(airportSeasons)
        appContext.handleSetIsLoading({ isLoading: false })
      },
      () =>
        appContext.handleSetIsLoading({ isLoading: false, fromFailure: true })
    )
    // eslint-disable-next-line
  }, [pageId])

  const [separateFiles, setSeparateFiles] = useState(false)
  const [separateDays, setSeparateDays] = useState(false)

  const processFlightReportResult = (newReports: FlightReports) => {
    const downloadReports: FlightReports = _.clone(appContext.flightReports)
    newReports.forEach((report) => downloadReports.push(report))
    appContext.handleChangeFlightReports(downloadReports)
    appContext.handleSetIsDownloading(false)

    if (showAlert) {
      store.dispatch(
        notify({
          kind: NotificationKind.INFO,
          title: PageContent.information,
          message: errorMsg
        })
      )
    }
  }

  const [checked, setChecked] = useState(null)

  const [touched, setTouched] = useState(false)

  const [fieldName, setFieldName] = useState("")
  const [error, setError] = useState(false)

  const [invalidReportArr, setInvalidReportArr] = useState([])
  const [validSum, setValidSum] = useState(["sumOf", "sumBy"])

  // const flightSelectorState = useFlightSelectorState()
  const [flightSelectorState, setFlightSelectorState] =
    useState<IFlightSelectorValues>(useFlightSelectorStateValues())
  const countReportPage = pageId === PageId.CountReport
  const downloadFlightsPage = pageId === PageId.DownloadFlights

  //Functions for Report flights
  const [reportState, setReportState] = useState({
    groupBy1: "",
    groupBy2: "",
    groupBy3: "",
    groupBy4: "",
    groupBy5: "",
    sumBy: "",
    sumOf: "",
    sumOf2: ""
  })

  const onReportValuesChanged = (
    field: string,
    value: string,
    error?: boolean
  ) => {
    if (!error) {
      setInvalidReportArr(invalidReportArr.filter((a) => a !== field))
      setValidSum(validSum.filter((a) => a !== field))
    } else {
      setInvalidReportArr([...invalidReportArr, field])
      setValidSum([...validSum, field])
    }

    const updReportState = _.cloneDeep(reportState)
    updReportState[field] = value !== null ? value : ""
    setReportState(updReportState)
  }

  const selectedAirports = airports.length
    ? airports
    : countries.length
    ? airportSeasons
        .filter(
          (as) =>
            as.season === season &&
            countries.indexOf(as.country) >= 0 &&
            (useLocalTime ? as.supportsLt : as.supportsUtc)
        )
        .map((a) => a.airport)
    : []

  const config = getFlightSelectorConfiguration(
    airportSeasons,
    season,
    selectedAirports,
    codeType
  )

  const getValues = (): IFlightSelectorValues => {
    return flightSelectorState
  }

  useEffect(() => {
    if (!config?.reportMultipleSumOf && reportState.sumOf2 !== "") {
      setReportState({ ...reportState, sumOf2: "" })
    }
  }, [config])

  //START Functions for handleErrors
  const operatorRestricted =
    (config && config.operatorRestricted) || getValues().useOwnOperators
  const operatorOptions = config && config.operators
  const isMulti = airportseasonSelectorConfig?.dropdownMultiSelect
  const selectedAirportsObj = {
    airportSeasons,
    airports,
    countries,
    season,
    useLocalTime
  }

  const {
    errors,
    showAlert,
    invalidFieldsArrs,
    errorMsg,
    lockedOrDisabledArr
  } = useErrorHandling(
    pageId,
    selectedAirportsObj,
    getValues(),
    fieldName,
    error,
    isMulti,
    EditMode.VIEW,
    operatorRestricted,
    operatorOptions
  )
  //END Functions for handleErrors

  const handleFlightSelectorFieldChange = (
    field: string,
    val: string,
    error: boolean
  ) => {
    const updFlightSelectorState = _.cloneDeep(flightSelectorState)
    updFlightSelectorState[field] = val
    setFlightSelectorState(updFlightSelectorState)
    setFieldName(field)
    setError(error)
    setTouched(false)
  }

  const handleDownloadTypes = (value: string) => {
    setChecked(value)
  }

  //Functions for submitting
  const handleGetReportParams = () => {
    const reportParameters = {
      groupByFields: [
        reportState.groupBy1,
        reportState.groupBy2,
        reportState.groupBy3,
        reportState.groupBy4,
        reportState.groupBy5
      ],
      sumByField: reportState.sumBy,
      sumOfFields:
        reportState.sumOf2 !== ""
          ? [reportState.sumOf, reportState.sumOf2]
          : [reportState.sumOf]
    }
    reportParameters[FieldName.useLocalTime] = useLocalTime

    return reportParameters
  }

  const handleSubmitDownload = () => {
    const invalidArr = Object.values(errors).filter((a) => a === true)
    const sendSubmit = invalidFieldsArrs.length === 0 && invalidArr.length === 0

    setTouched(true)

    const selectAirport = countReportPage
      ? selectedAirports.slice(0, 1)
      : selectedAirports
    const commitAirports = selectAirport.filter(
      (item) => !lockedOrDisabledArr.includes(item)
    )
    //#endregion

    const reportCountIsValid =
      reportState.sumBy !== "" &&
      reportState.sumOf !== "" &&
      invalidReportArr.length === 0
    setInvalidReportArr(validSum)

    const isValid = countReportPage
      ? sendSubmit && reportCountIsValid
      : sendSubmit

    if (isValid) {
      appContext.handleSetIsDownloading(true)

      const parameters = {
        ...flightSelectorState,
        addToQuery: false,
        season: season,
        airports: commitAirports,
        separateDays: downloadFlightsPage ? separateDays : false
      }
      handleDownload(
        pageId,
        parameters,
        countReportPage,
        checked,
        useLocalTime,
        downloadFlightsPage ? separateFiles : false,
        downloadFlightsPage ? separateDays : false,
        countReportPage ? handleGetReportParams() : {},
        processFlightReportResult,
        () => {
          appContext.handleSetIsDownloading(false)
        }
      )
    }
  }
  //END Functions for submitting

  const handleAirportChange = () => {
    const updFlightSelectorState = _.cloneDeep(flightSelectorState)
    updFlightSelectorState.useOwnOperators = false
    setFlightSelectorState(updFlightSelectorState)
  }

  const handleSeparateChange = (type: "airport" | "days", val: boolean) => {
    if (type === "airport") {
      setSeparateFiles(val)

      if (separateDays && val) {
        setSeparateDays(false)
      }
    }

    if (type === "days") {
      setSeparateDays(val)

      if (separateFiles && val) {
        setSeparateFiles(false)
      }
    }
  }

  return (
    <PageTemplate
      pageTitle={pageTitle}
      pageContentClassName="d-flex justify-content-center bg-none"
      pageClassName="ocs-download-bg"
    >
      <form
        className="ocs-form ocs-pt-20px ocs-pb-8px h-100p"
        onSubmit={(e) => e.preventDefault()}
      >
        <div className="d-flex h-100p">
          <section className="ocs-selector-container ocs-mr-16">
            <FlightSelector
              title={"Flight Selection"}
              titleSize={"sm"}
              airportSeasons={airportSeasons}
              handleChange={handleAirportChange}
              values={getValues()}
              onFieldChange={handleFlightSelectorFieldChange}
              flightSelectorConfig={flightSelectorConfig}
              airportseasonSelectorConfig={airportseasonSelectorConfig}
              codeType={codeType}
              pageId={pageId}
              errors={errors}
              touched={touched}
              hasError={
                errors.disabledOrlockedError || errors.emptyAirportField
              }
              useOwnOperatorError={errors.useOwnOperatorErrors}
              invalidDate={errors.singleDateError}
              errorMsg={errorMsg}
              onFlightSelectorChange={setFlightSelectorState}
            />
          </section>
          <div>
            <section className="ocs-selector-container config-selector">
              <div className="ocs-download-selector-container">
                <div className="ocs-selector-container-heading d-flex align-items-center justify-content-between">
                  <h2 className="ocs-heading sm">
                    {(countReportPage ? "Report" : "Download") +
                      " Configuration"}
                  </h2>
                </div>
                {!countReportPage ? (
                  <DownloadPane
                    airportSeasons={airportSeasons}
                    pageId={pageId}
                    onDownload={handleSubmitDownload}
                    checked={checked}
                    handleDownloadTypes={handleDownloadTypes}
                  />
                ) : (
                  <ReportSelector
                    values={reportState}
                    hasError={invalidReportArr}
                    multipleSumOf={config?.reportMultipleSumOf}
                    sumByFields={config?.reportSumByFields}
                    onChange={onReportValuesChanged}
                  />
                )}
                {downloadFlightsPage && (
                  <Card title="File">
                    <div className="ocs-mb-12 ocs-mt-12 d-flex col-gap-8">
                      <CheckBooleanField
                        name={FieldName.separateFiles}
                        value={separateFiles}
                        caption="Separate File per Airport"
                        onChange={(name, val) =>
                          handleSeparateChange("airport", val)
                        }
                      />
                      <CheckBooleanField
                        name={FieldName.separateDays}
                        value={separateDays}
                        caption="Separate File per Airport and Day"
                        onChange={(name, val) =>
                          handleSeparateChange("days", val)
                        }
                      />
                    </div>
                  </Card>
                )}
              </div>
            </section>
            <div className="d-flex justify-content-end ocs-mt-12px ocs-selector-container">
              <Button
                variant="primary"
                onClick={handleSubmitDownload}
                type="submit"
                disabled={countReportPage ? false : !checked}
              >
                {PageContent.download}
              </Button>
            </div>
          </div>
        </div>
      </form>
    </PageTemplate>
  )
}
